import { useState, useCallback, useRef, useEffect } from 'react';
import { workoutAPI, withPersonId, getActivePerson } from '../services/workoutAPI';

const DEBOUNCE_MS = 3000; // 3 seconds - faster saves while preventing spam

// Get person-specific localStorage key. Keyed on the person as well as the
// account: two people on one login each get their own local backup, so
// switching people can never restore the other person's in-progress workout.
// personId defaults to whoever is active now, but the unmount path passes the
// person the draft was actually typed under - see draftPersonRef below.
const getLocalStorageKey = (userId, personId = getActivePerson()) => {
  if (!userId) return null;
  return personId != null
    ? `workout_draft_backup_${userId}_p${personId}`
    : `workout_draft_backup_${userId}`;
};

// Helper: Save to localStorage synchronously
const saveToLocalStorage = (userId, draftData, personId) => {
  const key = getLocalStorageKey(userId, personId);
  if (!key) {
    console.log('⏸️ localStorage save skipped: no userId');
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify({
      ...draftData,
      savedAt: Date.now()
    }));
    console.log('💾 Draft backed up to localStorage');
  } catch (err) {
    console.error('Error saving to localStorage:', err);
  }
};

// Helper: Load from localStorage
const loadFromLocalStorage = (userId) => {
  const key = getLocalStorageKey(userId);
  if (!key) return null;
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    // Check if backup is < 24 hours old
    const ageHours = (Date.now() - parsed.savedAt) / (1000 * 60 * 60);
    if (ageHours > 24) {
      localStorage.removeItem(key);
      return null;
    }

    console.log('📥 Draft loaded from localStorage backup');
    return parsed;
  } catch (err) {
    console.error('Error loading from localStorage:', err);
    return null;
  }
};

// Helper: Clear localStorage backup
const clearLocalStorage = (userId) => {
  const key = getLocalStorageKey(userId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
    console.log('🗑️ localStorage backup cleared');
  } catch (err) {
    console.error('Error clearing localStorage:', err);
  }
};

export const useWorkoutDraft = (userId) => {
  const [hasDraft, setHasDraft] = useState(false);
  const [draftAge, setDraftAge] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const saveTimeoutRef = useRef(null);
  const lastDraftDataRef = useRef(null);
  // The person the pending draft was typed under. The unmount cleanup below
  // runs AFTER a person switch has already updated the active person (React
  // tears the old tree down after re-rendering with the new key), so using the
  // live active person there would file this draft under the person being
  // switched TO - overwriting theirs. Capture it at edit time instead.
  const draftPersonRef = useRef(null);

  // Check if draft exists (call this on mount)
  const checkDraft = useCallback(async () => {
    try {
      const draft = await workoutAPI.loadDraft();

      if (draft && draft.updated_at) {
        setHasDraft(true);

        // Calculate age
        const now = Date.now();
        const draftTime = new Date(draft.updated_at).getTime();
        const ageMs = now - draftTime;

        const hours = Math.floor(ageMs / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (hours < 1) {
          setDraftAge('less than an hour ago');
        } else if (hours < 24) {
          setDraftAge(`${hours} hour${hours > 1 ? 's' : ''} ago`);
        } else {
          setDraftAge(`${days} day${days > 1 ? 's' : ''} ago`);
        }
      } else {
        setHasDraft(false);
        setDraftAge(null);
      }

      return draft;
    } catch (err) {
      console.error('Error checking draft:', err);
      setHasDraft(false);
      return null;
    }
  }, []);

  // Load draft with full data
  const loadDraft = useCallback(async () => {
    setIsLoading(true);
    try {
      // Try server first
      const draft = await workoutAPI.loadDraft();
      console.log('📥 Draft loaded from server:', draft);

      if (draft) {
        // Clear localStorage backup if server has data
        clearLocalStorage(userId);
        return draft;
      }

      // Fallback to localStorage if server has no draft
      const localBackup = loadFromLocalStorage(userId);
      if (localBackup) {
        console.log('📦 Using localStorage backup (server had no draft)');
        return localBackup;
      }

      return null;
    } catch (err) {
      console.error('Error loading draft from server:', err);

      // If server fails, try localStorage
      const localBackup = loadFromLocalStorage(userId);
      if (localBackup) {
        console.log('📦 Using localStorage backup (server error)');
        return localBackup;
      }

      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save draft immediately (no debounce) - for critical moments
  const saveDraftImmediately = useCallback(async (workoutState) => {
    try {
      // Check if workout has meaningful data
      const hasName = workoutState.workoutName?.trim().length > 0;
      const hasExercises = workoutState.workoutExercises?.length > 0;
      const hasMeaningfulData = hasName || hasExercises;

      if (!hasMeaningfulData) {
        console.log('⏸️ Immediate save skipped: workout is empty');
        return false;
      }

      const draftData = {
        workoutName: workoutState.workoutName,
        workoutDate: workoutState.workoutDate,
        workoutTime: workoutState.workoutTime,
        workoutNotes: workoutState.workoutNotes,
        durationMinutes: workoutState.durationMinutes,
        elapsedSeconds: workoutState.elapsedSeconds,
        workoutExercises: workoutState.workoutExercises,
        programContext: workoutState.programContext || null
      };

      const personId = getActivePerson();
      draftPersonRef.current = personId;

      // Save to localStorage first (synchronous)
      saveToLocalStorage(userId, draftData, personId);

      // Then save to server with keepalive for reliability during unload
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      await fetch(withPersonId(`${API_URL}/api/workouts/draft`, personId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(draftData),
        keepalive: true // Ensures request completes even if page unloads
      });

      console.log('⚡ Draft saved immediately (no debounce)');
      setHasDraft(true);
      return true;
    } catch (err) {
      console.error('Error saving draft immediately:', err);
      // localStorage backup already saved, so not a complete failure
      return false;
    }
  }, []);

  // Save draft (debounced to 3 seconds)
  const saveDraft = useCallback((workoutState) => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Store for reference, along with the person it was typed under
    lastDraftDataRef.current = workoutState;
    draftPersonRef.current = getActivePerson();

    // Check if workout has meaningful data
    const hasName = workoutState.workoutName?.trim().length > 0;
    const hasExercises = workoutState.workoutExercises?.length > 0;
    const hasMeaningfulData = hasName || hasExercises;

    if (!hasMeaningfulData) {
      console.log('⏸️ Debounced save skipped: workout is empty');
      return;
    }

    // Save to localStorage immediately (no debounce for backup)
    const draftData = {
      workoutName: workoutState.workoutName,
      workoutDate: workoutState.workoutDate,
      workoutTime: workoutState.workoutTime,
      workoutNotes: workoutState.workoutNotes,
      durationMinutes: workoutState.durationMinutes,
      elapsedSeconds: workoutState.elapsedSeconds,
      workoutExercises: workoutState.workoutExercises,
      programContext: workoutState.programContext || null
    };
    saveToLocalStorage(userId, draftData, draftPersonRef.current);

    // Debounce the server save
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await workoutAPI.saveDraft(draftData);
        console.log('💾 Draft saved to server (debounced)');
        setHasDraft(true);
      } catch (err) {
        console.error('Error saving draft:', err);
        // localStorage backup already saved
      }
    }, DEBOUNCE_MS);
  }, []);

  // Clear draft
  const clearDraft = useCallback(async () => {
    // Clear pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Clear the last draft data ref to prevent saving on unmount
    lastDraftDataRef.current = null;
    draftPersonRef.current = null;

    // Clear localStorage backup
    clearLocalStorage(userId);

    try {
      await workoutAPI.clearDraft();
      console.log('🗑️ Draft cleared from server');
      setHasDraft(false);
      setDraftAge(null);
    } catch (err) {
      console.error('Error clearing draft:', err);
    }
  }, []);

  // Cleanup on unmount - save pending draft immediately
  useEffect(() => {
    return () => {
      if (lastDraftDataRef.current) {
        // Check if workout has meaningful data before saving
        const hasName = lastDraftDataRef.current.workoutName?.trim().length > 0;
        const hasExercises = lastDraftDataRef.current.workoutExercises?.length > 0;
        const hasMeaningfulData = hasName || hasExercises;

        // Only save if there's actual workout data
        if (!hasMeaningfulData) {
          console.log('⏸️ Unmount save skipped: workout is empty');
          return;
        }

        // Save to localStorage synchronously (guaranteed to complete)
        const draftData = {
          workoutName: lastDraftDataRef.current.workoutName,
          workoutDate: lastDraftDataRef.current.workoutDate,
          workoutTime: lastDraftDataRef.current.workoutTime,
          workoutNotes: lastDraftDataRef.current.workoutNotes,
          durationMinutes: lastDraftDataRef.current.durationMinutes,
          elapsedSeconds: lastDraftDataRef.current.elapsedSeconds,
          workoutExercises: lastDraftDataRef.current.workoutExercises,
          programContext: lastDraftDataRef.current.programContext || null
        };
        const personId = draftPersonRef.current;
        saveToLocalStorage(userId, draftData, personId);

        // Also attempt server save with keepalive
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        // Use fetch directly with keepalive for reliability during unmount
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        fetch(withPersonId(`${API_URL}/api/workouts/draft`, personId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(draftData),
          keepalive: true
        }).then(() => {
          console.log('💾 Draft saved on unmount (keepalive)');
        }).catch(err => {
          console.error('Error saving draft on unmount:', err);
          console.log('✅ localStorage backup is available');
        });
      }
    };
  }, [userId]);

  return {
    hasDraft,
    draftAge,
    isLoading,
    checkDraft,
    loadDraft,
    saveDraft,
    saveDraftImmediately, // NEW: Expose immediate save
    clearDraft,
  };
};
